import { useEffect } from "react";
import { useAtom } from "jotai";
import { useRouter } from "next/router";
import { utmParametersAtom, type UTMParameters } from "@contexts/utmAtoms";

export const useUTMParameters = () => {
	const router = useRouter();
	const [utmParameters, setUtmParameters] = useAtom(utmParametersAtom);

	useEffect(() => {
		if (!router.isReady) return;

		const url = new URL(window.location.href);
		const searchParams = url.searchParams;
		const utmParams: UTMParameters = {};
		const paramsToRemove: string[] = [];

		searchParams.forEach((value, key) => {
			if (["utm_campaign", "utm_source", "utm_medium"].includes(key)) {
				utmParams[key] = value;
				paramsToRemove.push(key);
			}
		});

		if (Object.keys(utmParams).length > 0) {
			setUtmParameters(utmParams);

			// GA page_view is already triggered by the initial gtag('config') call (with page_location),
			// so avoid firing an extra page_view here to prevent double-counting.

			for (const param of paramsToRemove) {
				searchParams.delete(param);
			}

			const newUrl =
				url.pathname +
				(searchParams.toString() ? `?${searchParams.toString()}` : "") +
				url.hash;

			window.history.replaceState({}, "", newUrl);
		}
	}, [router.isReady, setUtmParameters]);

	// Add UTM parameters to all link clicks
	useEffect(() => {
		const handleLinkClick = (event: MouseEvent) => {
			const target = event.target as HTMLElement;
			const link = target.closest("a");

			if (!link) return;

			const href = link.getAttribute("href");
			if (
				!href ||
				href.startsWith("#") ||
				href.startsWith("javascript:") ||
				href.startsWith("mailto:") ||
				href.startsWith("tel:")
			) {
				return;
			}

			// Don't modify if no UTM parameters exist
			if (Object.keys(utmParameters).length === 0) {
				return;
			}

			// Prevent default behavior to handle manually
			event.preventDefault();

			try {
				// Only add UTM parameters to URLs that contain "flx.mobi"
				if (href.startsWith("http") && href.includes("flx.mobi")) {
					const url = new URL(href);

					// Add UTM parameters if they don't already exist
					for (const [key, value] of Object.entries(utmParameters)) {
						if (value && !url.searchParams.has(key)) {
							if (key === "utm_campaign") {
								url.searchParams.set("~campaign", value);
								continue;
							}
							if (key === "utm_source") {
								url.searchParams.set("~channel", value);
								continue;
							}
							if (key === "utm_medium") {
								url.searchParams.set("~feature", value);
							}
						}
					}

					// Navigate to the modified URL
					if (link.target === "_blank") {
						window.open(url.toString(), "_blank");
					} else {
						window.location.href = url.toString();
					}
				} else {
					// For all other URLs, navigate normally without adding UTM parameters
					if (link.target === "_blank") {
						window.open(href, "_blank");
					} else {
						window.location.href = href;
					}
				}
			} catch (error) {
				// Fallback to original behavior if URL parsing fails
				console.warn("Failed to add UTM parameters to link:", error);
				if (link.target === "_blank") {
					window.open(href, "_blank");
				} else {
					window.location.href = href;
				}
			}
		};

		document.addEventListener("click", handleLinkClick);

		return () => {
			document.removeEventListener("click", handleLinkClick);
		};
	}, [utmParameters]);

	return utmParameters;
};
